<template>
  <section class="bg-secondary lg:px-48 md:px-12 px-4 pb-14 pt-16">
    <div class="max-w-2xl mx-auto">
      <template v-for="(block, idx) in blocks" :key="idx">

        <!-- Відео (type 14) -->
        <div v-if="block.type === '14' && (block.video_url || block.src)" class="my-8 aspect-video rounded-2xl overflow-hidden shadow-lg">
          <iframe
            :src="cleanVideoUrl(block.video_url || block.src)"
            class="w-full h-full"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            loading="lazy"
          />
        </div>

        <!-- Layout блоки (type 61/62) — type 19 тут пропускається -->
        <template v-else-if="block.type === '61' || block.type === '62'">

          <!-- block9: divider -->
          <hr v-if="block.data?.blockType === 'block9'" class="my-10 border-gray-200">

          <!-- block8: note / цитата -->
          <blockquote
            v-else-if="block.data?.blockType === 'block8' && getGroupText(block.data, 'noteGroup')"
            class="my-8 border-l-4 border-gray-800 pl-6 py-2"
          >
            <div
              class="font-pt-serif italic text-lg leading-relaxed text-gray-700"
              v-html="sanitize(getGroupText(block.data, 'noteGroup')!)"
            />
          </blockquote>

          <!-- block7: список -->
          <div v-else-if="block.data?.blockType === 'block7'" class="my-6">
            <h3
              v-if="getListTitle(block.data)"
              class="font-pt-serif text-2xl font-bold mb-4 leading-tight"
              v-html="sanitize(getListTitle(block.data)!)"
            />
            <ul class="font-pt-serif text-lg leading-relaxed space-y-3 pl-6 list-disc text-gray-800">
              <li
                v-for="(item, i) in getListItems(block.data)"
                :key="i"
                v-html="sanitize(item)"
              />
            </ul>
          </div>

          <!-- block6: тест -->
          <div
            v-else-if="block.data?.blockType === 'block6'"
            class="my-8 bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          >
            <div
              v-if="getBlockQuestion(block.data)"
              class="font-pt-serif text-xl font-bold leading-snug mb-6"
              v-html="sanitize(getBlockQuestion(block.data)!)"
            />

            <!-- userinput -->
            <textarea
              v-if="getTestType(block.data) === 'userinput'"
              v-model="textInputs[blockKey(block, idx)]"
              class="w-full border-b-2 border-gray-200 p-3 font-pt-serif text-lg resize-none focus:border-black outline-none transition-colors bg-transparent"
              rows="3"
              placeholder="..."
            />

            <!-- singlechoice -->
            <template v-if="getTestType(block.data) === 'singlechoice'">
              <div
                v-for="(card, ci) in getCards(block.data)"
                :key="ci"
                class="flex items-start gap-4 mb-4 cursor-pointer group"
                @click="singleAnswers[blockKey(block, idx)] = ci"
              >
                <div
                  class="flex-shrink-0 w-5 h-5 rounded-full border-2 mt-1 flex items-center justify-center transition-colors flex-shrink-0"
                  :class="singleAnswers[blockKey(block, idx)] === ci ? 'border-black bg-black' : 'border-gray-300 group-hover:border-gray-500'"
                >
                  <div v-if="singleAnswers[blockKey(block, idx)] === ci" class="w-2 h-2 rounded-full bg-white" />
                </div>
                <div
                  class="font-montserrat text-base leading-relaxed text-gray-700"
                  v-html="sanitize(card?.textField?.data?.text ?? card?.text ?? '')"
                />
              </div>
            </template>
          </div>

          <!-- block1–5, block10: основний текст -->
          <template v-else>
            <div
              v-for="(item, i) in extractTexts(block.data)"
              :key="i"
            >
              <h3
                v-if="item.isTitle"
                class="font-pt-serif text-2xl font-bold mt-10 mb-4 leading-tight"
                v-html="sanitize(item.text)"
              />
              <div
                v-else
                class="font-pt-serif text-lg leading-relaxed text-gray-800 mb-4"
                v-html="sanitize(item.text)"
              />
            </div>
          </template>

        </template>

        <!-- type 9: INPUT_TEXT (питання з полем вводу) -->
        <div
          v-else-if="block.type === '9' || block.type === 9"
          class="my-8 bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
        >
          <div
            v-if="block.text"
            class="font-pt-serif text-xl font-bold leading-snug mb-4"
            v-html="sanitize(block.text)"
          />
          <textarea
            v-model="textInputs[blockKey(block, idx)]"
            class="w-full border-b-2 border-gray-200 p-3 font-pt-serif text-lg resize-none focus:border-black outline-none transition-colors bg-transparent"
            rows="3"
            placeholder="..."
          />
        </div>

        <!-- Fallback: прямий текст -->
        <div v-else-if="block.text" class="font-pt-serif text-lg leading-relaxed mb-6 text-gray-800" v-html="sanitize(block.text)" />

      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import { reactive } from 'vue'
import type { LessonBlock } from '~/types/lesson.types'

const props = withDefaults(defineProps<{ blocks?: LessonBlock[] }>(), { blocks: () => [] })

// Стани відповідей (ключ — block.id або fallback на idx)
const singleAnswers = reactive<Record<string, number>>({})
const textInputs = reactive<Record<string, string>>({})

function cleanVideoUrl(raw: string): string {
  const trimmed = raw.trim()
  try {
    const url = new URL(trimmed)
    url.searchParams.set('rel', '0')
    url.searchParams.set('modestbranding', '1')
    return url.toString()
  } catch {
    return trimmed
  }
}

function sanitize(html: string): string {
  if (import.meta.server) {
    // SSR: strip script/iframe tags to prevent XSS in server-rendered HTML
    return html.replace(/<script[\s\S]*?<\/script>/gi, '')
               .replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
               .replace(/on\w+="[^"]*"/gi, '')
  }
  if (typeof window !== 'undefined' && (window as any).__dompurify) {
    return (window as any).__dompurify.sanitize(html)
  }
  return html
}

function getGroupText(blockData: any, groupName: string): string | null {
  const group = blockData?.fieldsGroups?.find((g: any) => g?.name === groupName)
  const field = group?.fields?.find((f: any) => f?.type === 'text' && f?.data?.text)
  return field?.data?.text ?? null
}

function extractTexts(blockData: any): Array<{ text: string; isTitle: boolean }> {
  const results: Array<{ text: string; isTitle: boolean }> = []
  for (const group of blockData?.fieldsGroups ?? []) {
    const isHiddenGroup = group?.fields?.find((f: any) => f?.name === 'isShown')?.data?.value === false
    const isTeacherOnly = group?.fields?.find((f: any) => f?.name === 'isShownOnlyInTeacherCabinet')?.data?.value === true
    if (isHiddenGroup || isTeacherOnly) continue
    for (const field of group?.fields ?? []) {
      if (field?.type === 'text' && field?.data?.text) {
        const isTitle = group.name === 'titleGroup' || field.name === 'title'
        results.push({ text: field.data.text, isTitle })
      }
    }
  }
  return results
}

function getTestType(blockData: any): string {
  for (const group of blockData?.fieldsGroups ?? []) {
    for (const field of group?.fields ?? []) {
      if (field?.type === 'testCards' || field?.type === 'cards') {
        return field?.data?.testType ?? 'userinput'
      }
    }
  }
  return 'userinput'
}

function getCards(blockData: any): any[] {
  for (const group of blockData?.fieldsGroups ?? []) {
    for (const field of group?.fields ?? []) {
      if (field?.type === 'testCards' || field?.type === 'cards') {
        return field?.data?.cardsCollection ?? []
      }
    }
  }
  return []
}

function getListTitle(blockData: any): string | null {
  const group = blockData?.fieldsGroups?.find((g: any) => g?.name === 'titleGroup')
  const isShown = group?.fields?.find((f: any) => f?.name === 'isShown')?.data?.value
  if (!isShown) return null
  const field = group?.fields?.find((f: any) => f?.name === 'title' && f?.data?.text)
  return field?.data?.text ?? null
}

function getListItems(blockData: any): string[] {
  const group = blockData?.fieldsGroups?.find((g: any) => g?.name === 'listGroup')
  const field = group?.fields?.find((f: any) => f?.name === 'listItems')
  const items = field?.data?.listCollection ?? []
  return items.map((item: any) => item?.textField?.text ?? item?.textField?.data?.text ?? '').filter(Boolean)
}

function blockKey(block: any, idx: number): string {
  return String(block.id ?? block._id ?? idx)
}

const GRADING_TYPES = new Set(['7', '8', '9', '60', '61', '62'])

function getResults(): Array<Record<string, unknown>> {
  return props.blocks
    .map((block: any, idx: number) => {
      const typeStr = String(block.type)
      const isBlock6 = (typeStr === '61' || typeStr === '62') && block.data?.blockType === 'block6'
      const isLegacyGrading = GRADING_TYPES.has(typeStr) && typeStr !== '61' && typeStr !== '62'

      if (!isBlock6 && !isLegacyGrading) return null

      const key = blockKey(block, idx)

      if (isBlock6) {
        const testType = getTestType(block.data)
        if (testType === 'singlechoice') {
          const answer = singleAnswers[key]
          if (answer === undefined) return null
          return { id: key, type: Number(typeStr), user_answers: answer }
        }
        const text = textInputs[key]
        if (!text?.trim()) return null
        return { id: key, type: Number(typeStr), user_answers: text }
      }

      // type 7 (singlechoice), 8 (multichoice), 9 (text input)
      const text = textInputs[key]
      if (!text?.trim()) return null
      return { id: key, type: Number(typeStr), user_answers: text }
    })
    .filter(Boolean) as Array<Record<string, unknown>>
}

defineExpose({ getResults })

function getBlockQuestion(blockData: any): string | null {
  for (const group of blockData?.fieldsGroups ?? []) {
    if (group.name === 'titleGroup') continue
    const isTeacherOnly = group?.fields?.find((f: any) => f?.name === 'isShownOnlyInTeacherCabinet')?.data?.value === true
    if (isTeacherOnly) continue
    for (const field of group?.fields ?? []) {
      if (field?.type === 'text' && field?.data?.text) return field.data.text
    }
  }
  return null
}
</script>
